import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Card } from './ui/card';
import { signatureDishes } from '../data/menu';

// Card animation variants
const cardVariants = {
  hidden: { 
    opacity: 0, 
    y: 60,
    rotateX: -15
  },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  })
};

const imageVariants = {
  hover: {
    scale: 1.15,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

export function SignatureDishes() {
  const { t, language, isRTL } = useLanguage();
  const dishes = signatureDishes[language];

  return (
    <section 
      id="dishes" 
      className="py-24 sm:py-32 bg-[#FDF8F4] overflow-hidden"
      data-testid="signature-dishes-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Stagger Animation */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, type: "spring" }}
            className={`font-cormorant text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6B1C23] mb-4 ${
              isRTL ? 'font-noto-serif-arabic' : ''
            }`}
            data-testid="dishes-title"
          >
            {t('dishes.title')}
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "100px" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-1 bg-[#6B1C23] mx-auto mb-4"
          />
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className={`font-manrope text-[#6B6B6B] text-lg ${isRTL ? 'font-cairo' : ''}`}
          >
            {t('dishes.subtitle')}
          </motion.p>
        </motion.div>

        {/* Dishes Grid with Staggered Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dishes.map((dish, index) => (
            <motion.div
              key={dish.id}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={cardVariants}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              style={{ perspective: 1000 }}
            >
              <Card 
                className="group bg-white border border-gray-200 hover:border-[#6B1C23] hover:shadow-2xl transition-all duration-500 overflow-hidden"
                data-testid={`dish-card-${index}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <motion.img
                    src={dish.image}
                    alt={dish.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    variants={imageVariants}
                    whileHover="hover"
                  />
                  {/* Gradient Overlay Animation */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"
                    initial={{ opacity: 0.4 }}
                    whileHover={{ opacity: 0.7 }}
                    transition={{ duration: 0.3 }}
                  />
                  {/* Shine Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full"
                    whileHover={{ translateX: "200%" }}
                    transition={{ duration: 0.6 }}
                  />
                </div>
                <motion.div 
                  className="p-6"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <motion.h3 
                    className={`font-cormorant text-xl font-semibold text-[#6B1C23] mb-2 ${
                      isRTL ? 'font-noto-serif-arabic' : ''
                    }`}
                    whileHover={{ x: isRTL ? -5 : 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {dish.name}
                  </motion.h3>
                  <p className={`font-manrope text-[#6B6B6B] text-sm ${isRTL ? 'font-cairo' : ''}`}>
                    {dish.description}
                  </p>
                </motion.div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
